import { startTransition, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCompanyData, RichTextContent } from '@vibesites/runtime/react';
import { motion, AnimatePresence } from 'framer-motion';
import { CommunityCard } from './CommunityCard';
import { CommunitiesFilters } from './CommunitiesFilters';
import { CommunitySearch } from './CommunitySearch';
import { CommunitiesMapCanvas } from './CommunitiesMapCanvas';
import { CommunitiesFilterBar } from './CommunitiesFilterBar';
import { addFavoriteIds, useCommunityFavoriteIds, useFavoriteCount } from './communityFavorites';
import { CommunitiesShareMenu } from './CommunitiesShareMenu';
import { useBottomSheet } from './useBottomSheet';
import {
  clearBrowseToast,
  clearCompare,
  selectCommunity,
  setBrowseFilters,
  setBrowseResults,
  setBrowseSort,
  setMobileBrowseView,
  showBrowseToast,
  useCommunitiesBrowse,
  type CommunitySort,
} from './communitiesBrowseStore';
import { isFilterActive, readUrlState, replaceUrl, SORT_OPTIONS, sortProperties } from './communitiesBrowseUtils';
import {
  COMMUNITY_BASE_PATH,
  COMMUNITY_LIFESTYLE_OPTIONS,
  defaultCommunityListFilters,
  FILTER_PRICE_BOUNDS,
  FILTER_SQFT_BOUNDS,
  formatCityWithState,
  formatFromPrice,
  getAvailableCities,
  getAvailableStates,
  getCommunityPath,
  getCommunityProperties,
  getStateName,
  getAllCityOptions,
  getTopMetroCities,
  parseCommunitySearchQuery,
  parseListFiltersFromSearchParams,
  propertyMatchesListFilters,
  propertyMatchesParsedSearch,
  type CommunityListFilters,
  type CommunityProperty,
} from './propertyUtils';

interface CommunitiesListProps {
  limit?: number;
  showFilters?: boolean;
  /** `split` renders the list beside a fixed map instead of below a separate map section. */
  layout?: 'stacked' | 'split';
}

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const media = window.matchMedia(query);
    const apply = () => setMatches(media.matches);
    apply();
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, [query]);
  return matches;
}

function useStickyHeaderOffset(enabled: boolean) {
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const header = document.querySelector('header');
    if (!header) return;
    const measure = () => {
      const { position } = getComputedStyle(header);
      setOffset(position === 'sticky' || position === 'fixed' ? header.getBoundingClientRect().height : 0);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    return () => observer.disconnect();
  }, [enabled]);
  return offset;
}

type ViewMode = 'grid' | 'list';

const SPLIT_BATCH_SIZE = 24;
type BrowseTab = 'list' | 'favorites';

function scrollToSection(id: string) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });
}

function navigateTo(url: string) {
  const nav = (window as unknown as { __vibesites_navigate?: (to: string) => void }).__vibesites_navigate;
  if (nav) {
    nav(url);
    return;
  }
  window.location.assign(url);
}

function GridIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden>
      <rect x="3" y="4" width="18" height="3.5" rx="1" />
      <rect x="3" y="10.25" width="18" height="3.5" rx="1" />
      <rect x="3" y="16.5" width="18" height="3.5" rx="1" />
    </svg>
  );
}

function HeartTabIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor" aria-hidden>
      <path d="M12 21s-6.7-4.35-9.33-7.6C.7 10.9 1.2 7.4 3.9 5.85 5.7 4.8 8 5.05 9.5 6.5L12 9l2.5-2.5c1.5-1.45 3.8-1.7 5.6-.65 2.7 1.55 3.2 5.05 1.23 7.55C18.7 16.65 12 21 12 21z" />
    </svg>
  );
}

function MapTabIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M9 4l-5 2v14l5-2 6 2 5-2V4l-5 2-6-2z" />
      <path d="M9 4v14M15 6v14" />
    </svg>
  );
}

function SkeletonCard() {
  return (
    <div className="overflow-hidden rounded-xl bg-[rgb(var(--ala-background))] shadow-[0_8px_28px_rgb(var(--ala-primary)/0.08)]">
      <div className="aspect-[3/2] animate-pulse bg-[rgb(var(--ala-surface))]" />
      <div className="space-y-3 p-4">
        <div className="h-5 w-2/3 animate-pulse rounded bg-[rgb(var(--ala-surface))]" />
        <div className="h-4 w-1/2 animate-pulse rounded bg-[rgb(var(--ala-surface))]" />
        <div className="h-4 w-1/3 animate-pulse rounded bg-[rgb(var(--ala-surface))]" />
      </div>
    </div>
  );
}

export function CommunitiesList({ limit, showFilters = true, layout = 'stacked' }: CommunitiesListProps) {
  const isSplit = layout === 'split';
  const companyData = useCompanyData();
  const urlState = readUrlState();
  const favoriteIds = useCommunityFavoriteIds();
  const favoriteCount = useFavoriteCount();
  const browse = useCommunitiesBrowse();
  const headerOffset = useStickyHeaderOffset(isSplit);
  const [listFilters, setListFilters] = useState<CommunityListFilters>(() => urlState.filters);
  const [searchQuery, setSearchQuery] = useState(urlState.search);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [browseTab, setBrowseTab] = useState<BrowseTab>('list');
  const [sharedFavoriteIds, setSharedFavoriteIds] = useState<string[] | null>(null);
  const isLargeScreen = useMediaQuery('(min-width: 1024px)');
  const useSheet = isSplit && !isLargeScreen;
  const sheetContainerRef = useRef<HTMLDivElement>(null);
  const sheet = useBottomSheet({ enabled: useSheet, containerRef: sheetContainerRef });
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [renderCount, setRenderCount] = useState(SPLIT_BATCH_SIZE);
  const listPaneRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const headerOffsetRef = useRef(0);
  headerOffsetRef.current = headerOffset;
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setBrowseFilters(listFilters);
  }, [listFilters]);

  useEffect(() => {
    setBrowseSort(urlState.sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    replaceUrl(
      listFilters,
      searchQuery,
      browse.sort,
      isSplit
        ? {
            basePath: window.location.pathname,
            hash: '',
            extraParams: sharedFavoriteIds ? { favorites: sharedFavoriteIds.join(',') } : undefined,
          }
        : undefined,
    );
  }, [listFilters, searchQuery, browse.sort, isSplit, sharedFavoriteIds]);

  useEffect(() => {
    if (!isSplit) return;
    const shared = new URLSearchParams(window.location.search).get('favorites');
    const ids = (shared || '').split(',').map((id) => id.trim()).filter(Boolean);
    if (ids.length === 0) return;
    setSharedFavoriteIds(ids);
    setBrowseTab('favorites');
  }, [isSplit]);

  useEffect(() => {
    if (!useSheet) return;
    setMobileBrowseView(sheet.snap === 'peek' ? 'map' : 'list');
  }, [useSheet, sheet.snap]);

  useEffect(() => {
    if (useSheet && browse.mobileView === 'map') sheet.setSnap('peek');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useSheet, browse.mobileView]);

  // Until the split view is pinned to the top of the viewport, wheel input scrolls the page
  // (not the list or the map's zoom), so sections above it like the hero can be scrolled past.
  useEffect(() => {
    const section = sectionRef.current;
    if (!isSplit || !section) return;
    const onWheel = (event: WheelEvent) => {
      if (!window.matchMedia('(min-width: 1024px)').matches) return;
      const top = section.getBoundingClientRect().top - headerOffsetRef.current;
      const deltaY =
        event.deltaMode === 1 ? event.deltaY * 16 : event.deltaMode === 2 ? event.deltaY * window.innerHeight : event.deltaY;
      const towardSection = (deltaY > 0 && top > 1) || (deltaY < 0 && top < -1);
      if (!towardSection) return;
      event.preventDefault();
      event.stopPropagation();
      const delta = deltaY > 0 ? Math.min(deltaY, top) : Math.max(deltaY, top);
      // `html { scroll-behavior: smooth }` would otherwise restart an animation on every wheel tick.
      window.scrollBy({ top: delta, behavior: 'instant' });
    };
    section.addEventListener('wheel', onWheel, { capture: true, passive: false });
    return () => section.removeEventListener('wheel', onWheel, { capture: true });
  }, [isSplit]);

  const exitSharedFavorites = () => {
    setSharedFavoriteIds(null);
    setBrowseTab('list');
  };

  const saveSharedFavorites = () => {
    if (!sharedFavoriteIds) return;
    const added = addFavoriteIds(sharedFavoriteIds);
    showBrowseToast(added > 0 ? `Added ${added} to your Favorites` : 'Already in your Favorites');
    setSharedFavoriteIds(null);
    setBrowseTab('favorites');
  };

  const applySearchParams = (params: URLSearchParams) => {
    setListFilters(parseListFiltersFromSearchParams(params));
    setSearchQuery((params.get('search') || '').trim());
    const sort = params.get('sort') as CommunitySort | null;
    setBrowseSort(sort && SORT_OPTIONS.some((option) => option.value === sort) ? sort : 'featured');
    setSharedFavoriteIds(null);
    setBrowseTab('list');
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.hash === '#communities-results') {
      document.getElementById('communities-results')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 639px)');
    const apply = () => {
      if (media.matches) setViewMode('grid');
    };
    apply();
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    if (!mobileFiltersOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileFiltersOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [mobileFiltersOpen]);

  const favoritesSource = sharedFavoriteIds ?? favoriteIds;
  const allProperties = useMemo(() => getCommunityProperties(companyData), [companyData]);
  const availableStates = useMemo(() => getAvailableStates(allProperties), [allProperties]);
  const availableCities = useMemo(() => getAvailableCities(allProperties), [allProperties]);
  const cityOptions = useMemo(() => getAllCityOptions(allProperties), [allProperties]);
  const metros = useMemo(() => getTopMetroCities(allProperties, 8), [allProperties]);

  const parsedSearch = useMemo(
    () => parseCommunitySearchQuery(searchQuery, availableCities, availableStates),
    [searchQuery, availableCities, availableStates],
  );

  const properties = useMemo(() => {
    const filtered = allProperties.filter((prop) => {
      if (browseTab === 'favorites') {
        const id = String(prop.multihub_property_id || '');
        if (!id || !favoritesSource.includes(id)) return false;
      }
      if (searchQuery && !propertyMatchesParsedSearch(prop, parsedSearch)) return false;
      return propertyMatchesListFilters(prop, listFilters);
    });
    return sortProperties(filtered, browse.sort, browse.userLocation);
  }, [
    allProperties,
    browseTab,
    favoritesSource,
    listFilters,
    parsedSearch,
    searchQuery,
    browse.sort,
    browse.userLocation,
  ]);

  useEffect(() => {
    const ids = properties
      .map((prop) => String(prop.multihub_property_id || ''))
      .filter(Boolean);
    setBrowseResults(ids, properties.length);
  }, [properties]);

  const limited = typeof limit === 'number' ? properties.slice(0, limit) : properties;
  const visible = isSplit ? limited.slice(0, renderCount) : limited;
  const hasMoreToRender = isSplit && renderCount < limited.length;
  const hasMore = typeof limit === 'number' && properties.length > limit;
  const hasActiveFilters = isFilterActive(listFilters, searchQuery);

  useEffect(() => {
    if (!isSplit) return;
    setRenderCount(SPLIT_BATCH_SIZE);
    const pane = listPaneRef.current;
    if (pane && pane.scrollHeight > pane.clientHeight) pane.scrollTo({ top: 0 });
  }, [isSplit, listFilters, searchQuery, browseTab, browse.sort]);

  useEffect(() => {
    if (!hasMoreToRender || !loadMoreRef.current) return;
    const pane = listPaneRef.current;
    const root = pane && getComputedStyle(pane).overflowY === 'auto' ? pane : null;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setRenderCount((count) => count + SPLIT_BATCH_SIZE);
        }
      },
      { root, rootMargin: '600px 0px' },
    );
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasMoreToRender, renderCount]);

  useEffect(() => {
    if (!isSplit || !browse.selectedId || browse.focusNonce <= 0) return;
    if (!window.matchMedia('(min-width: 1024px)').matches) return;
    const index = limited.findIndex((prop) => String(prop.multihub_property_id || '') === browse.selectedId);
    if (index < 0) return;
    if (index >= renderCount) setRenderCount(index + SPLIT_BATCH_SIZE);
    const selector = `[data-community-id="${CSS.escape(browse.selectedId)}"]`;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        listPaneRef.current?.querySelector(selector)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    });
    // Only react to explicit focus requests (pin clicks / "view on map"), not hover selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browse.focusNonce]);
  const compareProperties = useMemo(
    () =>
      browse.compareIds
        .map((id) => allProperties.find((prop) => String(prop.multihub_property_id || '') === id))
        .filter(Boolean) as CommunityProperty[],
    [allProperties, browse.compareIds],
  );

  const handleFiltersChange = (next: CommunityListFilters) => {
    if (isSplit) {
      startTransition(() => setListFilters(next));
      return;
    }
    setIsFiltering(true);
    startTransition(() => {
      setListFilters(next);
      setIsFiltering(false);
    });
  };

  const handleReset = () => {
    const next = defaultCommunityListFilters();
    setListFilters(next);
    setSearchQuery('');
    setBrowseSort('featured');
    setMobileFiltersOpen(false);
    if (isSplit) {
      setBrowseTab('list');
      return;
    }
    navigateTo(`${COMMUNITY_BASE_PATH}#communities-results`);
  };

  const clearChip = (chip: { type: string; value?: string | number }) => {
    if (chip.type === 'search') {
      setSearchQuery('');
      return;
    }
    if (chip.type === 'price') {
      handleFiltersChange({
        ...listFilters,
        priceMin: FILTER_PRICE_BOUNDS.min,
        priceMax: FILTER_PRICE_BOUNDS.max,
      });
      return;
    }
    if (chip.type === 'sqft') {
      handleFiltersChange({
        ...listFilters,
        sqftMin: FILTER_SQFT_BOUNDS.min,
        sqftMax: FILTER_SQFT_BOUNDS.max,
      });
      return;
    }
    if (chip.type === 'bed') {
      handleFiltersChange({
        ...listFilters,
        bedrooms: listFilters.bedrooms.filter((item) => item !== chip.value),
      });
      return;
    }
    if (chip.type === 'bath') {
      handleFiltersChange({
        ...listFilters,
        bathrooms: listFilters.bathrooms.filter((item) => item !== chip.value),
      });
      return;
    }
    if (chip.type === 'state') {
      handleFiltersChange({
        ...listFilters,
        states: listFilters.states.filter((item) => item !== chip.value),
      });
      return;
    }
    if (chip.type === 'city') {
      handleFiltersChange({
        ...listFilters,
        cities: (listFilters.cities ?? []).filter((item) => item !== chip.value),
      });
      return;
    }
    if (chip.type === 'lifestyle') {
      handleFiltersChange({
        ...listFilters,
        lifestyle: (listFilters.lifestyle ?? []).filter((item) => item !== chip.value),
      });
    }
  };

  const activeChips = useMemo(() => {
    const chips: { key: string; label: string; type: string; value?: string | number }[] = [];
    if (searchQuery) chips.push({ key: 'search', label: `“${searchQuery}”`, type: 'search' });
    if (
      listFilters.priceMin > FILTER_PRICE_BOUNDS.min ||
      listFilters.priceMax < FILTER_PRICE_BOUNDS.max
    ) {
      chips.push({
        key: 'price',
        label: `$${listFilters.priceMin.toLocaleString()}–$${listFilters.priceMax.toLocaleString()}`,
        type: 'price',
      });
    }
    if (
      listFilters.sqftMin > FILTER_SQFT_BOUNDS.min ||
      listFilters.sqftMax < FILTER_SQFT_BOUNDS.max
    ) {
      chips.push({
        key: 'sqft',
        label: `${listFilters.sqftMin.toLocaleString()}–${listFilters.sqftMax.toLocaleString()} sqft`,
        type: 'sqft',
      });
    }
    listFilters.bedrooms.forEach((bed) =>
      chips.push({
        key: `bed-${bed}`,
        label: bed === 0 ? 'Studio' : `${bed} Bed`,
        type: 'bed',
        value: bed,
      }),
    );
    listFilters.bathrooms.forEach((bath) =>
      chips.push({
        key: `bath-${bath}`,
        label: `${bath} Bath`,
        type: 'bath',
        value: bath,
      }),
    );
    listFilters.states.forEach((state) =>
      chips.push({
        key: `state-${state}`,
        label: getStateName(state.toUpperCase()),
        type: 'state',
        value: state,
      }),
    );
    (listFilters.cities ?? []).forEach((citySlug) => {
      const option = cityOptions.find((item) => item.slug === citySlug);
      chips.push({
        key: `city-${citySlug}`,
        label: option
          ? formatCityWithState(option.city, option.state)
          : citySlug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        type: 'city',
        value: citySlug,
      });
    });
    (listFilters.lifestyle ?? []).forEach((id) => {
      const option = COMMUNITY_LIFESTYLE_OPTIONS.find((item) => item.id === id);
      chips.push({
        key: `life-${id}`,
        label: option?.label || id,
        type: 'lifestyle',
        value: id,
      });
    });
    return chips;
  }, [listFilters, searchQuery, cityOptions]);

  const resultSummary = [
    `${properties.length} ${browseTab === 'favorites' ? (sharedFavoriteIds ? 'shared ' : 'saved ') : ''}communit${properties.length === 1 ? 'y' : 'ies'}`,
    listFilters.states.length === 1
      ? ` in ${getStateName(listFilters.states[0].toUpperCase())}`
      : listFilters.states.length > 1
        ? ` in ${listFilters.states.length} states`
        : '',
    searchQuery ? ` matching “${searchQuery}”` : '',
  ].join('');

  const activeFilterGroups = new Set(
    activeChips.filter((chip) => chip.type !== 'search').map((chip) => chip.type),
  ).size;

  const filtersProps = {
    filters: listFilters,
    states: availableStates,
    cities: cityOptions,
    resultCount: properties.length,
    onChange: handleFiltersChange,
    onReset: handleReset,
  };

  const emptyFavorites = browseTab === 'favorites' && favoritesSource.length === 0;
  const emptyFiltered = visible.length === 0 && !emptyFavorites;
  const hiddenOnMobileMap = browse.mobileView === 'map';
  const cardsGridClass = isSplit
    ? 'grid gap-4 sm:grid-cols-2 min-[1700px]:grid-cols-3'
    : 'grid gap-6 sm:grid-cols-2 xl:grid-cols-3';

  const listHeader = (
        <div
          className={
            isSplit
              ? 'mb-4 flex flex-col gap-4'
              : 'mb-6 flex flex-col gap-4 lg:mb-10 lg:flex-row lg:items-end lg:justify-between lg:gap-8'
          }
        >
          <div className="flex min-w-0 flex-wrap items-end justify-between gap-4 lg:shrink-0">
            <div className="min-w-0">
              <RichTextContent
                path="communitieslist.heading"
                label="Heading"
                as="h2"
                className={
                  isSplit
                    ? 'font-heading text-2xl font-medium tracking-[-0.02em] text-[rgb(var(--ala-text))]'
                    : 'font-heading text-[clamp(1.75rem,3vw,2.75rem)] font-medium tracking-[-0.03em] text-[rgb(var(--ala-text))]'
                }
              />
              {isSplit ? (
                <p className="mt-1 font-body text-sm text-[rgb(var(--ala-text-muted))]" aria-live="polite">
                  {resultSummary}
                </p>
              ) : (
                <RichTextContent
                  path="communitieslist.search_hint"
                  label="Search hint"
                  as="p"
                  className="mt-2 font-body text-sm text-[rgb(var(--ala-text-muted))]"
                />
              )}
            </div>
            <a
              href={COMMUNITY_BASE_PATH}
              className={
                hasMore
                  ? 'font-heading text-sm uppercase tracking-wide text-[rgb(var(--ala-secondary))] transition hover:opacity-80'
                  : 'hidden'
              }
              aria-hidden={!hasMore}
              tabIndex={hasMore ? undefined : -1}
            >
              <RichTextContent
                path="communitieslist.view_all_label"
                label="View all label"
                as="span"
              />
            </a>
          </div>
          {showFilters && !isSplit ? (
            <div className="min-w-0 w-full lg:max-w-xl lg:flex-1">
              <CommunitySearch placeholder="Try “pet friendly apartments in tampa”..." />
            </div>
          ) : null}
        </div>
  );

  return (
    <>
    <section
      ref={sectionRef}
      id="communities-results"
      className={
        isSplit
          ? 'flex h-[calc(100svh_-_var(--splittop))] flex-col overflow-hidden bg-[rgb(var(--ala-surface))]'
          : `bg-[rgb(var(--ala-surface))] pt-8 pb-28 lg:py-24 lg:pb-24 ${hiddenOnMobileMap ? 'max-lg:hidden' : ''}`
      }
      style={isSplit ? ({ '--splittop': `${headerOffset}px` } as CSSProperties) : undefined}
    >
      {isSplit && showFilters ? (
        <div className="relative z-30 shrink-0 border-b border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] px-4 py-3 md:px-6">
          <CommunitiesFilterBar
            filters={listFilters}
            states={availableStates}
            cities={cityOptions}
            onChange={handleFiltersChange}
            onReset={handleReset}
            onOpenAllFilters={() => setMobileFiltersOpen(true)}
            activeCount={activeFilterGroups}
            search={
              <CommunitySearch
                placeholder="Search by city, state, zip, or community"
                onApplySearchParams={applySearchParams}
              />
            }
          />
        </div>
      ) : null}
      <div className={isSplit ? 'relative min-h-0 flex-1 lg:flex' : undefined} ref={sheetContainerRef}>
      <div
        className={
          isSplit
            ? `z-20 min-w-0 overflow-y-auto bg-[rgb(var(--ala-surface))] px-4 pb-8 md:px-6 max-lg:absolute max-lg:inset-x-0 max-lg:bottom-0 max-lg:overscroll-contain max-lg:rounded-t-2xl max-lg:shadow-[0_-10px_30px_rgb(var(--ala-primary)/0.18)] lg:w-[60%] 2xl:w-[58%] ${
                sheet.dragging ? '' : 'max-lg:transition-[height] max-lg:duration-300 max-lg:ease-out'
              }`
            : 'mx-auto max-w-[1380px] px-6 md:px-12'
        }
        style={useSheet ? { height: sheet.height } : undefined}
        ref={listPaneRef}
      >
        {useSheet ? (
          <div className="sticky top-0 z-20 -mx-4 rounded-t-2xl bg-[rgb(var(--ala-surface))] px-4 md:-mx-6 md:px-6">
            <button
              type="button"
              aria-label={sheet.snap === 'full' ? 'Collapse results list' : 'Expand results list'}
              aria-expanded={sheet.snap !== 'peek'}
              {...sheet.handleProps}
              className="flex w-full touch-none select-none flex-col items-center gap-2 pb-3 pt-2.5"
            >
              <span aria-hidden className="h-1.5 w-10 rounded-full bg-[rgb(var(--ala-text))]/20" />
              <span className="font-heading text-sm font-semibold text-[rgb(var(--ala-text))]">{resultSummary}</span>
            </button>
          </div>
        ) : null}
        {!isSplit ? listHeader : null}

        <div
          className={
            !showFilters || isSplit
              ? ''
              : 'lg:grid lg:grid-cols-[280px_minmax(0,1fr)] lg:items-start lg:gap-8'
          }
        >
          {showFilters && !isSplit ? (
            <div className="sticky top-48 z-10 hidden max-h-[calc(100vh-14rem)] self-start overflow-y-auto overscroll-contain lg:block">
              <CommunitiesFilters {...filtersProps} />
            </div>
          ) : null}

          <div className="min-w-0">
            {showFilters ? (
              <div
                className={
                  isSplit
                    ? '-mx-4 mb-5 flex flex-wrap items-center gap-2 border-b border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] px-4 py-2.5 md:-mx-6 md:px-6 lg:sticky lg:top-0 lg:z-10'
                    : 'mb-6 flex flex-col gap-3'
                }
              >
                <div
                  className="inline-flex w-full overflow-hidden rounded-md border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] shadow-sm sm:w-auto"
                  role="tablist"
                  aria-label="Community browse"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={browseTab === 'list'}
                    onClick={() => setBrowseTab('list')}
                    className={`inline-flex flex-1 items-center justify-center gap-2 px-5 py-3 font-heading text-xs font-semibold uppercase tracking-wide transition sm:flex-none ${
                      browseTab === 'list'
                        ? 'bg-[rgb(var(--ala-primary))] text-[rgb(var(--ala-on-primary))]'
                        : 'text-[rgb(var(--ala-text-muted))] hover:bg-[rgb(var(--ala-surface))] hover:text-[rgb(var(--ala-text))]'
                    }`}
                  >
                    <ListIcon />
                    List
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={browseTab === 'favorites'}
                    onClick={() => setBrowseTab('favorites')}
                    className={`inline-flex flex-1 items-center justify-center gap-2 border-l border-[rgb(var(--ala-border))] px-5 py-3 font-heading text-xs font-semibold uppercase tracking-wide transition sm:flex-none ${
                      browseTab === 'favorites'
                        ? 'bg-[rgb(var(--ala-primary))] text-[rgb(var(--ala-on-primary))]'
                        : 'text-[rgb(var(--ala-text-muted))] hover:bg-[rgb(var(--ala-surface))] hover:text-[rgb(var(--ala-text))]'
                    }`}
                  >
                    <HeartTabIcon />
                    Favorites
                    {favoriteCount > 0 ? (
                      <span
                        className={`rounded-full px-1.5 py-0.5 font-body text-[10px] font-semibold normal-case tracking-normal ${
                          browseTab === 'favorites'
                            ? 'bg-[rgb(var(--ala-on-primary))]/20 text-[rgb(var(--ala-on-primary))]'
                            : 'bg-[rgb(var(--ala-surface))] text-[rgb(var(--ala-text-muted))]'
                        }`}
                      >
                        {favoriteCount}
                      </span>
                    ) : null}
                  </button>
                </div>

                <div className={isSplit ? 'flex items-stretch gap-2 sm:ml-auto' : 'flex flex-wrap items-stretch gap-3'}>
                  <button
                    type="button"
                    onClick={() => setMobileFiltersOpen(true)}
                    className={`inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-md border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] px-4 font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-text))] shadow-sm sm:flex-none ${isSplit ? 'hidden' : 'lg:hidden'}`}
                  >
                    Filters
                    {activeChips.length > 0 ? (
                      <span className="rounded-full bg-[rgb(var(--ala-primary))] px-1.5 py-0.5 font-body text-[10px] font-semibold normal-case tracking-normal text-[rgb(var(--ala-on-primary))]">
                        {activeChips.length}
                      </span>
                    ) : null}
                  </button>
                  <label className="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-md border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] px-3 shadow-sm">
                    <span className="font-heading text-[10px] font-semibold uppercase tracking-wide text-[rgb(var(--ala-text-muted))]">
                      Sort
                    </span>
                    <select
                      value={browse.sort}
                      onChange={(event) => setBrowseSort(event.target.value as CommunitySort)}
                      className="min-h-11 bg-transparent font-body text-sm text-[rgb(var(--ala-text))] outline-none"
                    >
                      {SORT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value} disabled={option.value === 'distance' && !browse.userLocation}>
                          {option.label}
                          {option.value === 'distance' && !browse.userLocation ? ' (use Near me)' : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                  <span className={`ml-auto self-center font-body text-xs text-[rgb(var(--ala-text-muted))] ${isSplit ? 'hidden' : 'lg:hidden'}`}>
                    {properties.length} result{properties.length === 1 ? '' : 's'}
                  </span>
                  <div
                    className="hidden shrink-0 self-stretch overflow-hidden rounded-md border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] shadow-sm sm:inline-flex"
                    role="group"
                    aria-label="View mode"
                  >
                    <button
                      type="button"
                      aria-pressed={viewMode === 'grid'}
                      aria-label="Grid view"
                      onClick={() => setViewMode('grid')}
                      className={`inline-flex flex-1 items-center justify-center gap-2 px-4 py-3 font-heading text-xs font-semibold uppercase tracking-wide transition sm:flex-none ${
                        viewMode === 'grid'
                          ? 'bg-[rgb(var(--ala-secondary))] text-[rgb(var(--ala-on-primary))]'
                          : 'text-[rgb(var(--ala-text-muted))] hover:bg-[rgb(var(--ala-surface))] hover:text-[rgb(var(--ala-text))]'
                      }`}
                    >
                      <GridIcon />
                      <span className={isSplit ? 'hidden 2xl:inline' : 'hidden sm:inline'}>Grid</span>
                    </button>
                    <button
                      type="button"
                      aria-pressed={viewMode === 'list'}
                      aria-label="List view"
                      onClick={() => setViewMode('list')}
                      className={`inline-flex flex-1 items-center justify-center gap-2 border-l border-[rgb(var(--ala-border))] px-4 py-3 font-heading text-xs font-semibold uppercase tracking-wide transition sm:flex-none ${
                        viewMode === 'list'
                          ? 'bg-[rgb(var(--ala-secondary))] text-[rgb(var(--ala-on-primary))]'
                          : 'text-[rgb(var(--ala-text-muted))] hover:bg-[rgb(var(--ala-surface))] hover:text-[rgb(var(--ala-text))]'
                      }`}
                    >
                      <ListIcon />
                      <span className={isSplit ? 'hidden 2xl:inline' : 'hidden sm:inline'}>Rows</span>
                    </button>
                  </div>
                  {isSplit ? (
                    <CommunitiesShareMenu
                      searchLabel={resultSummary}
                      hasActiveSearch={hasActiveFilters}
                      favoriteIds={favoriteIds}
                      onApplySavedSearch={applySearchParams}
                    />
                  ) : null}
                </div>
              </div>
            ) : null}

            {isSplit ? listHeader : null}

            {isSplit && sharedFavoriteIds && browseTab === 'favorites' ? (
              <div className="mb-5 flex flex-col gap-3 rounded-xl border border-[rgb(var(--ala-secondary))]/40 bg-[rgb(var(--ala-background))] p-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="font-body text-sm text-[rgb(var(--ala-text))]">
                  You're viewing a shared list of {sharedFavoriteIds.length} communit{sharedFavoriteIds.length === 1 ? 'y' : 'ies'}.
                </p>
                <div className="flex shrink-0 gap-2">
                  <button
                    type="button"
                    onClick={saveSharedFavorites}
                    className="inline-flex min-h-10 items-center rounded-md bg-[rgb(var(--ala-primary))] px-3 font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-on-primary))]"
                  >
                    Save to my favorites
                  </button>
                  <button
                    type="button"
                    onClick={exitSharedFavorites}
                    className="inline-flex min-h-10 items-center rounded-md border border-[rgb(var(--ala-border))] px-3 font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-text))]"
                  >
                    Browse all
                  </button>
                </div>
              </div>
            ) : null}

            {activeChips.length > 0 && browseTab === 'list' ? (
              <div className="mb-5 flex flex-wrap items-center gap-2">
                {activeChips.map((chip) => (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => clearChip(chip)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] px-2.5 py-1.5 font-body text-xs text-[rgb(var(--ala-text))] transition hover:border-[rgb(var(--ala-text))]/30"
                  >
                    <span>{chip.label}</span>
                    <span aria-hidden className="text-[rgb(var(--ala-text-muted))]">×</span>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={handleReset}
                  className="font-heading text-xs uppercase tracking-wide text-[rgb(var(--ala-text))] underline-offset-4 transition hover:underline"
                >
                  Reset Filters
                </button>
              </div>
            ) : null}

            {showFilters && !isSplit && hasActiveFilters && browseTab === 'list' ? (
              <div className="mb-6">
                <p className="font-body text-sm text-[rgb(var(--ala-text-muted))]">
                  Showing {properties.length} communit{properties.length === 1 ? 'y' : 'ies'}
                  {listFilters.states.length === 1
                    ? ` in ${getStateName(listFilters.states[0].toUpperCase())}`
                    : listFilters.states.length > 1
                      ? ` in ${listFilters.states.length} states`
                      : ''}
                  {searchQuery ? ` matching “${searchQuery}”` : ''}.
                </p>
              </div>
            ) : null}

            {emptyFavorites ? (
              <div className="rounded-xl border border-dashed border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] px-6 py-10 text-center">
                <RichTextContent
                  path="communitieslist.favorites_empty_label"
                  label="Favorites empty state"
                  as="p"
                  className="font-body text-[rgb(var(--ala-text-muted))]"
                />
                <button
                  type="button"
                  onClick={() => setBrowseTab('list')}
                  className="mt-4 font-heading text-xs uppercase tracking-wide text-[rgb(var(--ala-secondary))]"
                >
                  Browse communities
                </button>
              </div>
            ) : null}

            {emptyFiltered ? (
              <div className="rounded-xl border border-dashed border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] px-6 py-10">
                <RichTextContent
                  path="communitieslist.empty_label"
                  label="Empty state"
                  as="p"
                  className="font-body text-[rgb(var(--ala-text-muted))]"
                />
                <div className="mt-4 flex flex-wrap gap-3">
                  {activeChips[0] ? (
                    <button
                      type="button"
                      onClick={() => clearChip(activeChips[0])}
                      className="rounded-md border border-[rgb(var(--ala-border))] px-3 py-2 font-heading text-xs uppercase tracking-wide text-[rgb(var(--ala-text))]"
                    >
                      Clear {activeChips[0].label}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={handleReset}
                    className="rounded-md bg-[rgb(var(--ala-primary))] px-3 py-2 font-heading text-xs uppercase tracking-wide text-[rgb(var(--ala-on-primary))]"
                  >
                    Reset all filters
                  </button>
                  {metros[0] ? (
                    <button
                      type="button"
                      onClick={() =>
                        handleFiltersChange({
                          ...defaultCommunityListFilters(),
                          cities: [metros[0].slug],
                        })
                      }
                      className="rounded-md border border-[rgb(var(--ala-border))] px-3 py-2 font-heading text-xs uppercase tracking-wide text-[rgb(var(--ala-text))]"
                    >
                      Try {metros[0].city}
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {isFiltering ? (
              <div className={cardsGridClass}>
                {Array.from({ length: 6 }).map((_, index) => (
                  <SkeletonCard key={index} />
                ))}
              </div>
            ) : null}

            {!isFiltering && visible.length > 0 ? (
              <motion.div
                key={isSplit ? `${viewMode}-${browse.sort}-${browseTab}` : `${viewMode}-${browse.sort}-${properties.length}`}
                initial="hidden"
                animate="show"
                variants={{
                  hidden: {},
                  show: { transition: { staggerChildren: 0.045 } },
                }}
                className={
                  viewMode === 'list'
                    ? 'flex flex-col gap-5'
                    : cardsGridClass
                }
              >
                {visible.map((prop) => (
                  <motion.div
                    key={prop.multihub_property_id || getCommunityPathSafe(prop)}
                    variants={{
                      hidden: { opacity: 0, y: 12 },
                      show: { opacity: 1, y: 0, transition: { duration: 0.28 } },
                    }}
                    className={viewMode === 'grid' ? 'h-full' : undefined}
                  >
                    <CommunityCard property={prop} variant={viewMode} compactMedia={isSplit} />
                  </motion.div>
                ))}
              </motion.div>
            ) : null}

            {hasMoreToRender ? (
              <div ref={loadMoreRef} className="py-6 text-center font-body text-xs text-[rgb(var(--ala-text-muted))]">
                Loading more communities…
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {isSplit ? (
        <div
          id="communities-map"
          className="absolute inset-0 min-w-0 lg:relative lg:inset-auto lg:h-full lg:flex-1"
        >
          <CommunitiesMapCanvas
            compactControls
            className="h-full"
            heightClassName="h-full"
            mobilePreviewClassName="bottom-[148px]"
            errorLabel={
              <RichTextContent
                path="communitieslist.map_error_label"
                label="Map error"
                as="p"
                className="font-body text-sm text-[rgb(var(--ala-text-muted))]"
              />
            }
            emptyLabel={
              <RichTextContent
                path="communitieslist.map_empty_label"
                label="Map empty"
                as="p"
                className="font-body text-sm text-[rgb(var(--ala-text-muted))]"
              />
            }
          />
        </div>
      ) : null}
      </div>
    </section>

      <div className={`fixed inset-x-0 bottom-0 z-40 border-t border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))]/95 p-3 shadow-[0_-8px_24px_rgb(var(--ala-primary)/0.08)] backdrop-blur lg:hidden ${isSplit ? 'hidden' : ''}`}>
        <div
          className="mx-auto flex max-w-md overflow-hidden rounded-md border border-[rgb(var(--ala-border))]"
          role="group"
          aria-label="Map or list"
        >
          <button
            type="button"
            aria-pressed={browse.mobileView === 'map'}
            onClick={() => {
              setMobileBrowseView('map');
              scrollToSection('communities-map');
            }}
            className={`inline-flex flex-1 items-center justify-center gap-2 px-4 py-3 font-heading text-xs font-semibold uppercase tracking-wide ${
              browse.mobileView === 'map'
                ? 'bg-[rgb(var(--ala-primary))] text-[rgb(var(--ala-on-primary))]'
                : 'text-[rgb(var(--ala-text-muted))]'
            }`}
          >
            <MapTabIcon />
            Map
          </button>
          <button
            type="button"
            aria-pressed={browse.mobileView === 'list'}
            onClick={() => {
              setMobileBrowseView('list');
              scrollToSection('communities-results');
            }}
            className={`inline-flex flex-1 items-center justify-center gap-2 border-l border-[rgb(var(--ala-border))] px-4 py-3 font-heading text-xs font-semibold uppercase tracking-wide ${
              browse.mobileView === 'list'
                ? 'bg-[rgb(var(--ala-primary))] text-[rgb(var(--ala-on-primary))]'
                : 'text-[rgb(var(--ala-text-muted))]'
            }`}
          >
            <ListIcon />
            List
          </button>
        </div>
      </div>

      <AnimatePresence>
        {browse.toast ? (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            className={`fixed left-1/2 z-50 max-w-[calc(100%-2rem)] -translate-x-1/2 rounded-md bg-[rgb(var(--ala-primary))] px-4 py-2.5 font-body text-sm text-[rgb(var(--ala-on-primary))] shadow-lg ${
              compareProperties.length > 0 ? 'top-4' : 'bottom-20 lg:bottom-8'
            }`}
            role="status"
          >
            {browse.toast}
            <button type="button" className="ml-3 underline" onClick={clearBrowseToast}>
              Dismiss
            </button>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {compareProperties.length > 0 ? (
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            className="fixed inset-x-0 bottom-[4.75rem] z-30 px-3 lg:bottom-4 lg:px-6"
          >
            <div className="mx-auto max-w-[1380px] rounded-xl border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] p-4 shadow-[0_12px_40px_rgb(var(--ala-primary)/0.16)]">
              <div className="mb-3 flex items-center justify-between gap-3">
                <p className="font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-text))]">
                  Compare ({compareProperties.length}/3)
                </p>
                <button
                  type="button"
                  onClick={clearCompare}
                  className="font-heading text-xs uppercase tracking-wide text-[rgb(var(--ala-text-muted))] hover:text-[rgb(var(--ala-text))]"
                >
                  Clear
                </button>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-1 lg:grid lg:grid-cols-3 lg:overflow-visible">
                {compareProperties.map((prop) => {
                  const id = String(prop.multihub_property_id || '');
                  const flagsNote = [
                    formatFromPrice(prop),
                    formatBedsLine(prop),
                    prop.property_address_city,
                  ]
                    .filter(Boolean)
                    .join(' · ');
                  return (
                    <div
                      key={id}
                      className="w-[min(78vw,240px)] shrink-0 rounded-md border border-[rgb(var(--ala-border))] p-3 lg:w-auto"
                    >
                      <p className="font-heading text-sm font-semibold text-[rgb(var(--ala-text))]">
                        {prop.property_name}
                      </p>
                      <p className="mt-1 font-body text-xs text-[rgb(var(--ala-text-muted))]">
                        {flagsNote}
                      </p>
                      <div className="mt-3 flex gap-2">
                        <a
                          href={getCommunityPath(prop)}
                          className="font-heading text-[11px] uppercase tracking-wide text-[rgb(var(--ala-secondary))]"
                        >
                          Details
                        </a>
                        <button
                          type="button"
                          onClick={() => {
                            selectCommunity(id, { focus: true });
                            setMobileBrowseView('map');
                            scrollToSection('communities-map');
                          }}
                          className="font-heading text-[11px] uppercase tracking-wide text-[rgb(var(--ala-text-muted))]"
                        >
                          Map
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {showFilters && mobileFiltersOpen && isSplit ? (
        <div
          aria-hidden
          onClick={() => setMobileFiltersOpen(false)}
          className="fixed inset-0 z-50 hidden bg-[rgb(var(--ala-primary))]/40 lg:block"
        />
      ) : null}
      {showFilters && mobileFiltersOpen ? (
        <div
          className={`fixed inset-0 z-50 flex flex-col bg-[rgb(var(--ala-background))] ${isSplit ? 'lg:left-auto lg:w-[440px] lg:border-l lg:border-[rgb(var(--ala-border))] lg:shadow-2xl' : 'lg:hidden'}`}
          role="dialog"
          aria-modal="true"
          aria-label="Filters"
        >
          <div className="flex items-center justify-between gap-3 border-b border-[rgb(var(--ala-border))] px-4 py-3">
            <p className="font-heading text-sm font-bold uppercase tracking-wide text-[rgb(var(--ala-text))]">
              Filters
            </p>
            <button
              type="button"
              aria-label="Close filters"
              onClick={() => setMobileFiltersOpen(false)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-md text-[rgb(var(--ala-text-muted))] transition hover:bg-[rgb(var(--ala-surface))] hover:text-[rgb(var(--ala-text))]"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-5">
            <CommunitiesFilters {...filtersProps} embedded />
          </div>
          <div className="flex items-center gap-3 border-t border-[rgb(var(--ala-border))] p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex min-h-11 items-center justify-center rounded-md border border-[rgb(var(--ala-border))] px-4 font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-text))]"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={() => setMobileFiltersOpen(false)}
              className="inline-flex min-h-11 flex-1 items-center justify-center rounded-md bg-[rgb(var(--ala-primary))] px-4 font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-on-primary))]"
            >
              Show {properties.length} communit{properties.length === 1 ? 'y' : 'ies'}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}

function formatBedsLine(prop: CommunityProperty) {
  const min = prop.property_bedrooms_min;
  const max = prop.property_bedrooms_max || min;
  if (min == null) return '';
  if (String(min) === String(max)) return `${min === '0' || min === 0 ? 'Studio' : `${min} Bed`}`;
  return `${min}–${max} Bed`;
}

function getCommunityPathSafe(prop: { _stateSlug?: string; _citySlug?: string; _slug?: string }) {
  return `${COMMUNITY_BASE_PATH}/${prop._stateSlug}/${prop._citySlug}/${prop._slug}`;
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CommunitiesList',
  displayName: 'Communities List',
  tokens: {
    'communitieslist.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Find your next home</p>',
    },
    'communitieslist.search_hint': {
      label: 'Search hint',
      type: 'richtext',
      defaultValue: '<p>Search by community, city, state, and zipcode</p>',
    },
    'communitieslist.view_all_label': {
      label: 'View all label',
      type: 'richtext',
      defaultValue: '<p>View all communities</p>',
    },
    'communitieslist.empty_label': {
      label: 'Empty state',
      type: 'richtext',
      defaultValue: '<p>No communities match this search.</p>',
    },
    'communitieslist.favorites_empty_label': {
      label: 'Favorites empty state',
      type: 'richtext',
      defaultValue: '<p>No favorites yet. Tap the heart on a community to save it here.</p>',
    },
    'communitieslist.map_error_label': {
      label: 'Map error (split layout)',
      type: 'richtext',
      defaultValue: '<p>Map unavailable. Browse communities on the left.</p>',
    },
    'communitieslist.map_empty_label': {
      label: 'Map empty (split layout)',
      type: 'richtext',
      defaultValue: '<p>No mapped communities match these filters.</p>',
    },
  },
};

(CommunitiesList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default CommunitiesList;
